import type { ReportQuery, ReportQueryResult } from "@gettactic/api";
import { REPORT_QUERY_KEY } from "@gettactic/hooks";
import {
  keepPreviousData,
  type QueryClient,
  useQuery
} from "@tanstack/react-query";
import { api } from "@/lib/api/api";

export const useReportQueryKey = REPORT_QUERY_KEY;

const runReportQuery = async (
  query: ReportQuery
): Promise<ReportQueryResult> => {
  const { result } = await api.client.reporting.query(query);
  if (!result) {
    throw new Error("empty report query response");
  }
  return result;
};

/** One report query as TanStack options, for `useQueries` and prefetching. */
export const reportQueryOptions = (query: ReportQuery) => ({
  queryKey: [useReportQueryKey, query],
  queryFn: () => runReportQuery(query),
  staleTime: 60 * 1000
});

/** The same cache entry the hook uses, for code that runs outside a render (exports). */
export const fetchReportQuery = (
  queryClient: QueryClient,
  query: ReportQuery
): Promise<ReportQueryResult> =>
  queryClient.fetchQuery(reportQueryOptions(query));

/**
 * Runs one report query. The query object is the cache key, so the same
 * question asked from two places shares one answer; the previous answer stays
 * on screen while a changed question loads.
 */
export const useReportQuery = (query: ReportQuery | null) =>
  useQuery<ReportQueryResult>({
    queryKey: [useReportQueryKey, query],
    queryFn: () => {
      if (!query) {
        throw new Error("report query is required");
      }
      return runReportQuery(query);
    },
    enabled: !!query,
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000
  });
